import * as React from 'react';
import {useState} from 'react';

export interface Tab {
    label: string;
    content: React.ReactNode;
}

export interface TabContainerProps {
    /** Array of tabs to render. Each tab has a label and content node. */
    tabs: Tab[];
    /** Index of the tab to show initially. Defaults to 0. */
    defaultActiveIndex?: number;
    /** Called when the user switches to a different tab (not on initial render). */
    onChange?: (label: string, index: number) => void;
}

/**
 * Simple tab container component.
 * Renders tab headers and displays the active tab's content.
 * Used as the extensibility point for additionalTabs from backend config.
 */
const TabContainer: React.FC<TabContainerProps> = ({tabs, defaultActiveIndex = 0, onChange}) => {
    const clampedIndex = (!Array.isArray(tabs) || tabs.length === 0)
        ? 0
        : Math.max(0, Math.min(defaultActiveIndex, tabs.length - 1));
    const [activeIndex, setActiveIndex] = useState(clampedIndex);

    if (!Array.isArray(tabs) || tabs.length === 0) {
        return null;
    }

    return (
        <div className="lighthouse-tabs" data-testid="tab-container">
            <div className="lighthouse-tab-headers" data-testid="tab-headers">
                {tabs.map((tab, index) => (
                    <button
                        key={`tab-header-${index}`}
                        className={`lighthouse-tab-header ${index === activeIndex ? 'active' : ''}`}
                        onClick={() => {
                            if (index !== activeIndex) {
                                onChange?.(tab.label, index);
                            }
                            setActiveIndex(index);
                        }}
                        data-testid={`tab-header-${index}`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
            <div className="lighthouse-tab-content" data-testid="tab-content">
                {tabs[activeIndex]?.content}
            </div>
        </div>
    );
};

export default TabContainer;
